import type * as Y from 'yjs';

import type { Revision, RevisionType } from '../types';
import { isYValue, memoYjsTransform, type YJSInput, yToJSON } from '../utils';
import type { YRevision } from './revisionToY';

/**
 * Convert a YRevision into a Revision.
 * This function is optimized for performances when used on the same YRevision,
 * instead of doing only call to `yToJSON`, it wraps the calls by memoized groups.
 */
export const yToRevision = memoYjsTransform((yMap: YRevision): Revision => {
    return {
        id: yMap.get('id') as string,
        schemaVersion: yMap.get('schemaVersion') as number,
        type: yMap.get('type') as RevisionType,
        contentId: yMap.get('contentId') as string,
        conflicts: yToJSON(yMap.get('conflicts')),
        parents: yMap.get('parents').toArray(),
        mergedFrom: yMap.get('mergedFrom'),
        rolledBackBy: yMap.get('rolledBackBy'),
        git: yToJSON(yMap.get('git')),
        rootPages: yMap.get('rootPages').toArray(),
        pages: yToPages(yMap.get('pages')),
        files: yToFiles(yMap.get('files')),
        edits: yToJSON(yMap.get('edits')),
        variables: yToJSON(yMap.get('variables')),
        reusableContents: yToJSON(yMap.get('reusableContents')),
        tags: yToJSON(yMap.get('tags')),
    };
});

const yToPages = memoYjsTransform((yMap: Y.Map<any>): Revision['pages'] => {
    const result: Revision['pages'] = {};
    yMap.forEach((yPage, id) => {
        // Hack: in case of corrupted revisions in Yjs where objects are stored instead of Yjs values
        result[id] = isYValue(yPage) ? yToJSONCached(yPage) : yToJSON(yPage);
    });
    return result;
});
const yToFiles = memoYjsTransform((yMap: Y.Map<any>): Revision['files'] => {
    const result: Revision['files'] = {};
    yMap.forEach((yFile, id) => {
        // Hack: in case of corrupted revisions in Yjs where objects are stored instead of Yjs values
        result[id] = isYValue(yFile) ? yToJSONCached(yFile) : yToJSON(yFile);
    });
    return result;
});

const yToJSONCached = memoYjsTransform((yMap: YJSInput): any => {
    return yToJSON(yMap);
});
